import { Link } from 'react-router-dom';
import type { DashboardMetrics } from '@harbor/shared';
import { useApi } from '../../lib/hooks.js';
import { money, percentFromBps } from '../../lib/format.js';
import { Alert, Badge, Card, Skeleton, Stat } from '../../components/ui.js';

/**
 * Admin overview.
 *
 * ## The chart is inline SVG, not a charting library
 *
 * A bar chart of fourteen values is about thirty lines of SVG. A charting
 * library is 40–90 KB gzipped, brings its own theming system, and would need its
 * colours kept in sync with the design tokens. For a single chart the library is
 * a net loss.
 *
 * The comparison is against the *previous* thirty days, not the previous month.
 * Month-over-month compares 31 days to 28 and reads as growth in February; a
 * rolling window compares like with like.
 */
export function Dashboard() {
  const { data, loading, error } = useApi<DashboardMetrics>('/api/admin/metrics');

  if (loading && !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-40" />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-28 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert tone="danger" title="Could not load metrics">
        {error.message}
      </Alert>
    );
  }

  if (!data) return null;

  const peak = Math.max(1, ...data.revenueByDay.map((day) => day.revenueMinor));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Overview</h1>
        <p className="mt-1 text-sm text-muted">
          Revenue counts orders in <code>paid</code> or <code>fulfilled</code>. Cancelled and refunded
          orders are excluded here and nowhere else.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Revenue (30 days)"
          value={money(data.revenueMinor)}
          delta={percentFromBps(data.revenueChangeBps)}
          tone={data.revenueChangeBps >= 0 ? 'positive' : 'danger'}
        />
        <Stat
          label="Orders (30 days)"
          value={String(data.orderCount)}
          delta={percentFromBps(data.orderChangeBps)}
          tone={data.orderChangeBps >= 0 ? 'positive' : 'danger'}
        />
        <Stat label="Average order value" value={money(data.averageOrderValueMinor)} />
        <Stat
          label="Needs attention"
          value={String(data.lowStockCount + data.pendingCount)}
          delta={`${data.lowStockCount} low stock · ${data.pendingCount} open`}
          tone={data.lowStockCount > 0 ? 'warning' : 'neutral'}
        />
      </div>

      <Card className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">Revenue, last 14 days</h2>
          <p className="tabular text-xs text-subtle">Peak {money(peak)}</p>
        </div>

        {data.revenueByDay.length === 0 ? (
          <p className="mt-6 text-sm text-muted">
            No paid orders in this window. Place one from the storefront with the{' '}
            <code>tok_visa</code> test card.
          </p>
        ) : (
          <div className="mt-5">
            <div className="flex h-40 items-end gap-1.5" role="img" aria-label="Revenue by day for the last fourteen days">
              {data.revenueByDay.map((day) => {
                const height = Math.max(2, Math.round((day.revenueMinor / peak) * 100));
                return (
                  <div key={day.date} className="group relative flex flex-1 flex-col items-center justify-end">
                    <div
                      className="w-full rounded-t bg-accent/80 transition-colors group-hover:bg-accent"
                      style={{ height: `${height}%` }}
                    />
                    {/* The tooltip is a sibling element rather than a title
                        attribute so it can be styled and read by a screen reader. */}
                    <span className="tabular pointer-events-none absolute -top-7 hidden whitespace-nowrap rounded border border-line bg-surface px-2 py-1 text-xs shadow-sm group-hover:block">
                      {money(day.revenueMinor)}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 flex justify-between text-xs text-subtle">
              <span>{data.revenueByDay[0]?.date}</span>
              <span>{data.revenueByDay.at(-1)?.date}</span>
            </div>
          </div>
        )}
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="overflow-hidden">
          <div className="border-b border-line px-5 py-4">
            <h2 className="text-sm font-semibold">Best sellers</h2>
            <p className="mt-0.5 text-xs text-subtle">By units sold, all time.</p>
          </div>
          {data.topProducts.length === 0 ? (
            <p className="px-5 py-6 text-sm text-muted">Nothing sold yet.</p>
          ) : (
            <ul className="divide-y divide-line">
              {data.topProducts.map((product, index) => (
                <li key={product.slug || product.name} className="flex items-center gap-3 px-5 py-3">
                  <span className="tabular w-5 text-xs text-subtle">{index + 1}</span>
                  <div className="min-w-0 flex-1">
                    {product.slug ? (
                      <Link to={`/product/${product.slug}`} className="truncate text-sm hover:text-accent">
                        {product.name}
                      </Link>
                    ) : (
                      <span className="truncate text-sm">{product.name}</span>
                    )}
                  </div>
                  <span className="tabular text-xs text-muted">{product.unitsSold} units</span>
                  <span className="tabular w-20 text-right text-sm">
                    {money(product.revenueMinor)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="text-sm font-semibold">Operating notes</h2>
          <ul className="mt-4 space-y-3 text-sm text-muted">
            <li className="flex items-start gap-2.5">
              <Badge tone="warning">Reservations</Badge>
              <span>
                Stock is decremented when an order is created and released if it is not paid within
                30 minutes. A scheduled run sweeps abandoned checkouts every 15 minutes.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <Badge tone="neutral">Ledger</Badge>
              <span>
                Every stock movement is recorded with the resulting balance, so a discrepancy can
                always be traced back to a specific order or adjustment.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <Badge tone="accent">Payments</Badge>
              <span>
                Running the mock provider. Set <code>PAYMENT_PROVIDER=stripe</code> and a test-mode
                secret key to exercise the real webhook path.
              </span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
